import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays } from "lucide-react";

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [students, setStudents] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/meetings"), api.get("/students")]).then(([m, s]) => {
      setMeetings(m.data);
      const map = {};
      s.data.forEach((stu) => (map[stu.id] = stu));
      setStudents(map);
      setLoading(false);
    });
  }, []);

  const now = new Date();
  const upcoming = meetings.filter((m) => new Date(m.scheduled_at) >= now);
  const past = meetings.filter((m) => new Date(m.scheduled_at) < now);

  return (
    <div className="space-y-10" data-testid="meetings-page">
      <header>
        <div className="overline">Calendar</div>
        <h1 className="font-heading text-4xl font-medium ink mt-3">Meetings</h1>
        <p className="ink-muted mt-2 max-w-xl">Every IEP meeting in one place. Schedule new meetings from a student's file.</p>
      </header>

      {loading && <div className="ink-muted calm-pulse">Loading…</div>}

      {!loading && (
        <>
          <Section title="Upcoming" items={upcoming} students={students} empty="No upcoming meetings yet." />
          <Section title="Past" items={past} students={students} empty="No past meetings yet." />
        </>
      )}
    </div>
  );
}

function Section({ title, items, students, empty }) {
  return (
    <section>
      <h3 className="font-heading text-2xl font-medium ink mb-4">{title}</h3>
      {items.length === 0 ? (
        <Card className="p-8 border-dashed border-[#D8D9D5] bg-white text-center">
          <p className="ink-muted">{empty}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((m) => {
            const s = students[m.student_id];
            return (
              <Link key={m.id} to={`/students/${m.student_id}`} data-testid={`meetings-row-${m.id}`}>
                <Card className="p-5 border border-[#E8E9E6] bg-white lift">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#EFEAF5] flex items-center justify-center">
                      <CalendarDays size={18} className="text-[#6E5AA0]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium ink">{m.title}</div>
                      <div className="text-xs ink-muted mt-1">
                        {new Date(m.scheduled_at).toLocaleString()} · {m.duration_minutes} min
                      </div>
                      {s && (
                        <div className="mt-2 flex items-center gap-2">
                          <Badge variant="outline" className="bg-[#EFEAF5]">{s.first_name} {s.last_name}</Badge>
                          <span className="text-xs ink-muted">Grade {s.grade}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
